import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles, getFeaturedArticles } from '@/mocks/articles';
import { siteImages } from '@/mocks/images';

export default function Home() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const featuredArticles = getFeaturedArticles(5);
  const recentArticles = articles.slice(0, 6);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d9spcddlbrt1qcgaj7i0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.meta?.detail || responseText || '订阅失败，请重试';
        setFormStatus('error');
        setFormError(msg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '心灵家缘',
    url: 'https://xinlingjiayuan.com',
    description: '围绕「年轻的小姨子」这一家庭角色，深入探讨姐夫与小姨子之间如何建立健康的家庭边界、维护和谐亲情关系',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://xinlingjiayuan.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '心灵家缘',
    url: 'https://xinlingjiayuan.com',
    description: '专注家庭情感关系研究，帮助每个家庭成员找到恰当的角色定位',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressCountry: 'CN',
      streetAddress: '朝阳区建国路88号',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@xinlingjiayuan.com',
      contactType: 'customer service',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([schemaData, orgSchema]) }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={siteImages.hero}
              alt="家庭和谐相处 - 心灵家缘"
              title="心灵家缘 — 家庭情感边界与相处之道"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/35"></div>

          <div className="relative z-10 w-full text-center px-4 md:px-6 max-w-4xl mx-auto">
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              年轻的小姨子 — <br className="md:hidden" />家庭情感边界与相处之道
            </h1>
            <p className="text-white/80 text-sm md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              围绕<strong className="text-white">「年轻的小姨子」</strong>这一家庭角色，
              深入探讨<strong className="text-white">家庭情感边界</strong>的建立与维护，
              提供实用的<strong className="text-white">家庭相处技巧</strong>与情感沟通建议
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/xiangchu-zhidao"
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full text-sm transition-all whitespace-nowrap"
              >
                开始阅读
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 bg-background-50/20 hover:bg-background-50/30 text-white backdrop-blur-sm font-medium rounded-full text-sm transition-all border border-white/30 whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        {/* Categories Section */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-3">
                探索分类
              </h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
                围绕家庭关系中的不同维度，找到你最关心的话题
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex items-start gap-4 p-5 bg-background-50 rounded-xl border border-background-200/60 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-xl bg-primary-100 group-hover:bg-primary-200 transition-colors">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-xl text-primary-600`}></i>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-1 whitespace-nowrap">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 mb-2">{cat.description}</p>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{cat.articleCount} 篇文章</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles Section */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-1">
                  精选推荐
                </h2>
                <p className="text-sm text-foreground-500">编辑精心挑选的优质内容</p>
              </div>
              <Link
                to="/category/xiangchu-zhidao"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors whitespace-nowrap"
              >
                查看全部
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featuredArticles.slice(0, 3).map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
            <div className="mt-5 space-y-4">
              {featuredArticles.slice(3, 5).map((article) => (
                <ArticleCard key={article.id} article={article} variant="horizontal" />
              ))}
            </div>
          </div>
        </section>

        {/* Recent Articles + Sidebar */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-10">
              {/* Main Content */}
              <div className="flex-1">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                  最新文章
                </h2>
                <div className="space-y-4">
                  {recentArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} variant="horizontal" />
                  ))}
                </div>
              </div>

              {/* Sidebar */}
              <aside className="w-full lg:w-80 flex-shrink-0">
                <div className="lg:sticky lg:top-24 space-y-6">
                  {/* Popular Articles */}
                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">
                      热门文章
                    </h3>
                    <div className="space-y-3">
                      {articles.slice(8, 13).map((article) => (
                        <ArticleCard key={article.id} article={article} variant="compact" />
                      ))}
                    </div>
                  </div>

                  {/* Newsletter */}
                  <div className="bg-primary-50 rounded-xl p-5 border border-primary-100/60">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 mb-3">
                      <i className="ri-mail-send-line text-primary-600 text-lg"></i>
                    </div>
                    <h4 className="font-heading text-base font-semibold text-foreground-900 mb-1">
                      订阅我们的通讯
                    </h4>
                    <p className="text-sm text-foreground-500 mb-4">
                      获取最新的家庭关系建议和文章更新
                    </p>

                    {formStatus === 'success' ? (
                      <div className="text-sm text-secondary-700 bg-secondary-100 rounded-lg px-4 py-3">
                        订阅成功！感谢您的关注。
                      </div>
                    ) : (
                      <form ref={formRef} onSubmit={handleSubscribe} data-readdy-form="">
                        <input
                          type="text"
                          name="phone_alt"
                          className="form-honeypot"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                        <div className="flex gap-2">
                          <input
                            type="email"
                            name="email"
                            placeholder="输入您的邮箱"
                            required
                            className="flex-1 px-3 py-2 text-sm border border-background-300 rounded-lg focus:outline-none focus:border-primary-400 bg-background-50 text-foreground-900 placeholder:text-foreground-400"
                          />
                          <button
                            type="submit"
                            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                          >
                            订阅
                          </button>
                        </div>
                        {formStatus === 'error' && (
                          <p className="text-xs text-red-600 mt-2">{formError}</p>
                        )}
                      </form>
                    )}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20 bg-background-950">
          <div className="max-w-3xl mx-auto text-center px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-3xl font-bold text-background-50 mb-4">
              开始改善你的家庭关系
            </h2>
            <p className="text-background-300 text-sm md:text-base mb-8 max-w-lg mx-auto leading-relaxed">
              每个家庭都值得拥有和谐温暖的关系。从今天开始，用智慧和爱心经营你的家庭情感生活。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/jiefu-xiaoyizi"
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full text-sm transition-all whitespace-nowrap"
              >
                浏览文章
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 bg-transparent hover:bg-background-800 text-background-200 font-medium rounded-full text-sm transition-all border border-background-600 whitespace-nowrap"
              >
                关于我们
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}